import React, {useEffect, useState} from 'react';
import Layout from '../../Layout';
import {
    AlertCircle,
    BookOpen,
    Calendar,
    CheckCircle,
    ChevronRight,
    Clock,
    Eye,
    FileText,
    Filter,
    Globe,
    Loader,
    MessageCircle,
    Play,
    Search,
    Target,
    X
} from 'lucide-react';
import lessonAPI from '../../services/LessonAPI';

const StudentLessons = () => {
    // State management
    const [lessons, setLessons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notification, setNotification] = useState(null);

    // Filter state
    const [filters, setFilters] = useState({
        search: '',
        domain: '',
        grade: ''
    });

    // Pagination
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalLessons, setTotalLessons] = useState(0);
    const limit = 12;

    // Modal state
    const [selectedLesson, setSelectedLesson] = useState(null);
    const [showLessonModal, setShowLessonModal] = useState(false);

    useEffect(() => {
        fetchLessons();
    }, [page, filters]);

    const fetchLessons = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await lessonAPI.getPublishedLessons({
                page,
                limit,
                // Updated Logic: If grade is "NONE" or undefined/empty, send null
                gradeFilter: (filters.grade && filters.grade !== "NONE") ? filters.grade : null,
                domainFilter: filters.domain || null,
                search: filters.search || null
            });

            if (response && response.lessonPlans) {
                setLessons(response.lessonPlans);
                setTotalLessons(response.total);
                setTotalPages(Math.ceil(response.total / limit));
            } else {
                throw new Error("Invalid response format");
            }
        } catch (err) {
            console.error("Error fetching lessons:", err);
            setError("Failed to load lessons. Please try again.");
        } finally {
            setLoading(false);
        }
    };
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({...prev, [key]: value}));
        setPage(1);
    };

    const clearFilters = () => {
        setFilters({search: '', domain: '', grade: ''});
        setPage(1);
    };

    const viewLesson = async (lessonId) => {
        try {
            setLoading(true);
            const lesson = await lessonAPI.getPublishedLesson(lessonId);
            setSelectedLesson(lesson);
            setShowLessonModal(true);
        } catch (err) {
            console.error("Error loading lesson:", err);
            showNotification('error', 'Failed to load lesson details');
        } finally {
            setLoading(false);
        }
    };

    const showNotification = (type, message) => {
        setNotification({type, message});
        setTimeout(() => setNotification(null), 5000);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getDomainIcon = (domain) => {
        const domainMap = {
            'Mathematics': <Target className="w-5 h-5 text-blue-600"/>,
            'Language': <BookOpen className="w-5 h-5 text-purple-600"/>,
            'Science': <FileText className="w-5 h-5 text-green-600"/>,
            'Social Studies': <Globe className="w-5 h-5 text-amber-600"/>,
            'Arts': <Play className="w-5 h-5 text-pink-600"/>,
            'Physical Education': <CheckCircle className="w-5 h-5 text-red-600"/>
        };
        return domainMap[domain] || <BookOpen className="w-5 h-5 text-gray-600"/>;
    };

    const renderLessonContent = (lesson) => {
        return (
            <div className="space-y-6">
                {/* Basic Information */}
                <div className="bg-gray-50 rounded-xl p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <FileText className="w-5 h-5 text-blue-600"/>
                        Lesson Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <span className="font-semibold text-gray-700">Subject:</span>
                            <span className="ml-2 text-gray-600">{lesson.domain || 'Not specified'}</span>
                        </div>
                        <div>
                            <span className="font-semibold text-gray-700">Grade:</span>
                            <span
                                className="ml-2 text-gray-600">{lesson.classGrade ? `Grade ${lesson.classGrade}` : 'Not specified'}</span>
                        </div>
                        <div>
                            <span className="font-semibold text-gray-700">Duration:</span>
                            <span className="ml-2 text-gray-600">{lesson.duration || 'Not specified'}</span>
                        </div>
                        <div>
                            <span className="font-semibold text-gray-700">Date:</span>
                            <span className="ml-2 text-gray-600">{lesson.lessonDate || 'Not specified'}</span>
                        </div>
                    </div>
                    {lesson.themeTopic && (
                        <div className="mt-4">
                            <span className="font-semibold text-gray-700">Topic:</span>
                            <span className="ml-2 text-gray-600">{lesson.themeTopic}</span>
                        </div>
                    )}
                </div>

                {/* Learning Outcomes */}
                {(lesson.outcome1 || lesson.outcome2 || lesson.outcome3) && (
                    <div className="bg-blue-50 rounded-xl p-6">
                        <h3 className="text-xl font-bold text-blue-900 mb-4 flex items-center gap-2">
                            <Target className="w-5 h-5 text-blue-600"/>
                            What You'll Learn
                        </h3>
                        <div className="space-y-3">
                            {lesson.outcome1 && (
                                <div className="flex items-start gap-2">
                                    <ChevronRight className="w-4 h-4 text-blue-600 mt-1 flex-shrink-0"/>
                                    <p className="text-blue-800">{lesson.outcome1}</p>
                                </div>
                            )}
                            {lesson.outcome2 && (
                                <div className="flex items-start gap-2">
                                    <ChevronRight className="w-4 h-4 text-blue-600 mt-1 flex-shrink-0"/>
                                    <p className="text-blue-800">{lesson.outcome2}</p>
                                </div>
                            )}
                            {lesson.outcome3 && (
                                <div className="flex items-start gap-2">
                                    <ChevronRight className="w-4 h-4 text-blue-600 mt-1 flex-shrink-0"/>
                                    <p className="text-blue-800">{lesson.outcome3}</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Lesson Flow - Panchaadi Method */}
                {lesson.lessonFlowData && lesson.lessonFlowData.length > 0 && (
                    <div className="bg-green-50 rounded-xl p-6">
                        <h3 className="text-xl font-bold text-green-900 mb-4 flex items-center gap-2">
                            <Play className="w-5 h-5 text-green-600"/>
                            Lesson Activities (5-Step Method)
                        </h3>
                        <div className="space-y-4">
                            {lesson.lessonFlowData.map((step, index) => (
                                <div key={index} className="bg-white rounded-lg p-4 border border-green-200">
                                    <div className="flex items-center gap-3 mb-3">
                                        <div
                                            className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                                            {index + 1}
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-green-900">{step.stage}</h4>
                                            <p className="text-sm text-green-700">{step.time}</p>
                                        </div>
                                    </div>
                                    <div className="ml-11 space-y-2">
                                        {step.teacherRole && (
                                            <div>
                                                <span className="font-medium text-gray-700">Teacher:</span>
                                                <span className="ml-2 text-gray-600">{step.teacherRole}</span>
                                            </div>
                                        )}
                                        {step.studentRole && (
                                            <div>
                                                <span className="font-medium text-gray-700">You will:</span>
                                                <span className="ml-2 text-gray-600">{step.studentRole}</span>
                                            </div>
                                        )}
                                        {step.activityExamples && (
                                            <div>
                                                <span className="font-medium text-gray-700">Activities:</span>
                                                <span className="ml-2 text-gray-600">{step.activityExamples}</span>
                                            </div>
                                        )}
                                        {step.resources && (
                                            <div>
                                                <span className="font-medium text-gray-700">Resources:</span>
                                                <span className="ml-2 text-gray-600">{step.resources}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Pedagogical Principles */}
                {lesson.pedagogicalPrinciples && Object.values(lesson.pedagogicalPrinciples).some(val => val) && (
                    <div className="bg-purple-50 rounded-xl p-6">
                        <h3 className="text-xl font-bold text-purple-900 mb-4 flex items-center gap-2">
                            <MessageCircle className="w-5 h-5 text-purple-600"/>
                            Teaching Approach
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {lesson.pedagogicalPrinciples.homeLanguage && (
                                <div
                                    className="flex items-center gap-2 p-3 bg-white rounded-lg border border-purple-200">
                                    <CheckCircle className="w-4 h-4 text-purple-600"/>
                                    <span className="text-purple-800">Uses Home Language</span>
                                </div>
                            )}
                            {lesson.pedagogicalPrinciples.joyfulLearning && (
                                <div
                                    className="flex items-center gap-2 p-3 bg-white rounded-lg border border-purple-200">
                                    <CheckCircle className="w-4 h-4 text-purple-600"/>
                                    <span className="text-purple-800">Joyful Learning</span>
                                </div>
                            )}
                            {lesson.pedagogicalPrinciples.integrateDomains && (
                                <div
                                    className="flex items-center gap-2 p-3 bg-white rounded-lg border border-purple-200">
                                    <CheckCircle className="w-4 h-4 text-purple-600"/>
                                    <span className="text-purple-800">Integrated Learning</span>
                                </div>
                            )}
                            {lesson.pedagogicalPrinciples.simpleComplex && (
                                <div
                                    className="flex items-center gap-2 p-3 bg-white rounded-lg border border-purple-200">
                                    <CheckCircle className="w-4 h-4 text-purple-600"/>
                                    <span className="text-purple-800">Simple to Complex</span>
                                </div>
                            )}
                            {lesson.pedagogicalPrinciples.individualPace && (
                                <div
                                    className="flex items-center gap-2 p-3 bg-white rounded-lg border border-purple-200">
                                    <CheckCircle className="w-4 h-4 text-purple-600"/>
                                    <span className="text-purple-800">Individual Pace</span>
                                </div>
                            )}
                            {lesson.pedagogicalPrinciples.equalParticipation && (
                                <div
                                    className="flex items-center gap-2 p-3 bg-white rounded-lg border border-purple-200">
                                    <CheckCircle className="w-4 h-4 text-purple-600"/>
                                    <span className="text-purple-800">Equal Participation</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Structured Rich Text Content */}
                {lesson.structuredRichTextContent?.pages && lesson.structuredRichTextContent.pages.length > 0 && (
                    <div className="bg-gray-50 rounded-xl p-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <BookOpen className="w-5 h-5 text-blue-600"/>
                            Additional Content
                        </h3>
                        <div className="space-y-4">
                            {lesson.structuredRichTextContent.pages.map((page) => (
                                <div key={page.id} className="bg-white rounded-lg p-4 border border-gray-200">
                                    <h4 className="font-semibold text-gray-800 mb-3">{page.title}</h4>
                                    <div className="space-y-3">
                                        {page.sections.map((section) => (
                                            <div key={section.id}>
                                                <h5 className="font-medium text-gray-700 mb-2">{section.title}</h5>
                                                <div
                                                    className="prose prose-sm max-w-none text-gray-600"
                                                    dangerouslySetInnerHTML={{__html: section.content}}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Rich Text Content (Legacy) */}
                {lesson.richTextContent && (
                    <div className="bg-gray-50 rounded-xl p-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <BookOpen className="w-5 h-5 text-blue-600"/>
                            Lesson Notes
                        </h3>
                        <div
                            className="prose prose-sm max-w-none text-gray-600"
                            dangerouslySetInnerHTML={{__html: lesson.richTextContent}}
                        />
                    </div>
                )}

                {/* No Content Message */}
                {!lesson.outcome1 && !lesson.outcome2 && !lesson.outcome3 &&
                    (!lesson.lessonFlowData || lesson.lessonFlowData.length === 0) &&
                    (!lesson.structuredRichTextContent?.pages || lesson.structuredRichTextContent.pages.length === 0) &&
                    !lesson.richTextContent && (
                        <div className="text-center py-8 text-gray-500">
                            <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300"/>
                            <p>This lesson is still being prepared by your teacher.</p>
                            <p className="text-sm mt-2">Check back later for detailed content!</p>
                        </div>
                    )}
            </div>
        );
    };

    return (
        <Layout>
            <div className="bg-white p-8 rounded-b-2xl rounded-tr-2xl shadow-xl font-[poppins] min-h-full">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

                    {/* Notification */}
                    {notification && (
                        <div
                            className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center gap-3 transition-all duration-300 ${
                                notification.type === 'success' ? 'bg-green-500 text-white' :
                                    notification.type === 'error' ? 'bg-orange-500 text-white' :
                                        'bg-blue-500 text-white'
                            }`}>
                            {notification.type === 'success' ? <CheckCircle className="w-5 h-5"/> :
                                notification.type === 'error' ? <AlertCircle className="w-5 h-5"/> :
                                    <AlertCircle className="w-5 h-5"/>}
                            <span className="font-[poppins]">{notification.message}</span>
                            <button onClick={() => setNotification(null)} className="ml-2 hover:opacity-80">
                                <X className="w-4 h-4"/>
                            </button>
                        </div>
                    )}

                    {/* Header */}
                    <div className="pt-8 pb-8 text-center relative overflow-hidden">
                        <div className="relative z-10">
                            <div>
                                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6"
                                     style={{backgroundColor: '#D67C2C'}}>
                                    <BookOpen className="w-8 h-8 text-white"/>
                                </div>
                                <h1 className="text-5xl font-bold bg-[#D67C2C] bg-clip-text text-transparent mb-4">
                                    My Lessons
                                </h1>
                                <p className="text-[#1D3469] text-xl font-medium max-w-3xl mx-auto px-4">
                                    Explore lessons shared by your teachers
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Filter Section */}
                    <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200 mb-8">
                        <div className="p-6 border-b border-gray-100 bg-gray-50">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                                    <Filter className="w-5 h-5 text-white"/>
                                </div>
                                <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">Find Lessons</h2>
                            </div>
                        </div>

                        <div className="p-6">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700">
                                        <Search className="inline-block w-4 h-4 mr-2"/>
                                        Search
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            value={filters.search}
                                            onChange={(e) => handleFilterChange('search', e.target.value)}
                                            placeholder="Search lessons..."
                                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                                        />
                                        <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400"/>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700">
                                        <BookOpen className="inline-block w-4 h-4 mr-2"/>
                                        Subject
                                    </label>
                                    <select
                                        value={filters.domain}
                                        onChange={(e) => handleFilterChange('domain', e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                                    >
                                        <option value="">All Subjects</option>
                                        <option value="Mathematics">Mathematics</option>
                                        <option value="Language">Language</option>
                                        <option value="Science">Science</option>
                                        <option value="Social Studies">Social Studies</option>
                                        <option value="Arts">Arts</option>
                                        <option value="Physical Education">Physical Education</option>
                                    </select>
                                </div>

                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700">
                                        <Target className="inline-block w-4 h-4 mr-2"/>
                                        Grade
                                    </label>
                                    <select
                                        value={filters.grade}
                                        onChange={(e) => handleFilterChange('grade', e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                                    >
                                        <option value="NONE">My Grade</option>
                                        <option value="BV1">Pre-K (BV1)</option>
                                        <option value="BV2">Nursery (BV2)</option>
                                        <option value="BV3">Kindergarten (BV3)</option>
                                        <option value="1">Grade 1</option>
                                        <option value="2">Grade 2</option>
                                        <option value="3">Grade 3</option>
                                        <option value="4">Grade 4</option>
                                        <option value="5">Grade 5</option>
                                        <option value="6">Grade 6</option>
                                        <option value="7">Grade 7</option>
                                        <option value="8">Grade 8</option>
                                        <option value="9">Grade 9</option>
                                        <option value="10">Grade 10</option>
                                        <option value="11">Grade 11</option>
                                        <option value="12">Grade 12</option>
                                    </select>
                                </div>

                                <div className="flex items-end">
                                    <button
                                        onClick={clearFilters}
                                        className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                                    >
                                        Clear Filters
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Lessons Grid */}
                    {loading ? (
                        <div className="flex justify-center items-center p-12">
                            <Loader className="w-8 h-8 text-[#D67C2C] animate-spin"/>
                            <span className="ml-3 text-lg text-gray-600">Loading lessons...</span>
                        </div>
                    ) : error ? (
                        <div className="flex justify-center items-center p-12 text-red-500">
                            <AlertCircle className="w-6 h-6 mr-2"/>
                            <span>{error}</span>
                        </div>
                    ) : lessons.length === 0 ? (
                        <div className="text-center py-12">
                            <BookOpen className="w-16 h-16 mx-auto mb-4 text-gray-300"/>
                            <p className="text-lg font-medium text-gray-500">No lessons available</p>
                            <p className="text-sm text-gray-400 mb-4">
                                {filters.search || filters.domain || filters.grade ?
                                    "Try adjusting your filters to find lessons." :
                                    "Your teachers haven't published any lessons for your grade yet."}
                            </p>
                            {!filters.search && !filters.domain && !filters.grade && (
                                <div className="bg-blue-50 rounded-lg p-4 max-w-md mx-auto">
                                    <p className="text-sm text-blue-700">
                                        <strong>Note:</strong> You can see lessons that your teachers have published for
                                        students or completed in class.
                                        Draft lessons are not visible here.
                                    </p>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {lessons.map((lesson) => (
                                <div key={lesson.id}
                                     className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                                    <div className="p-6">
                                        {/* Lesson Header */}
                                        <div className="flex items-start gap-4 mb-4">
                                            <div
                                                className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
                                                {getDomainIcon(lesson.domain)}
                                            </div>
                                            <div className="flex-1">
                                                <h3 className="text-lg font-bold text-gray-900 mb-1">
                                                    {lesson.publishSettings?.publishedTitle || lesson.themeTopic || 'Untitled Lesson'}
                                                </h3>
                                                <p className="text-sm text-gray-600">
                                                    {lesson.publishSettings?.publishedDescription || lesson.themeTopic || 'No description available'}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Lesson Details */}
                                        <div className="space-y-3 mb-6">
                                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                                <BookOpen className="w-4 h-4 text-blue-600"/>
                                                <span>{lesson.domain || 'General'}</span>
                                                <span className="text-gray-400">•</span>
                                                <span>{lesson.classGrade ? `Grade ${lesson.classGrade}` : 'All Grades'}</span>
                                            </div>

                                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                                <Calendar className="w-4 h-4 text-green-600"/>
                                                <span>Published {formatDate(lesson.publishSettings?.publishedAt)}</span>
                                            </div>

                                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                                <Clock className="w-4 h-4 text-purple-600"/>
                                                <span>{lesson.duration || 'Duration not specified'}</span>
                                            </div>
                                        </div>

                                        {/* Status Badge */}
                                        <div className="mb-4">
                                            {lesson.status === 'published' && (
                                                <span
                                                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                    <CheckCircle className="w-3 h-3 mr-1"/>
                                                    Published
                                                </span>
                                            )}
                                            {lesson.status === 'taught' && (
                                                <span
                                                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                    <Clock className="w-3 h-3 mr-1"/>
                                                    Completed in Class
                                                </span>
                                            )}
                                        </div>

                                        {/* Learning Outcomes Preview */}
                                        {lesson.outcome1 && (
                                            <div className="bg-blue-50 rounded-lg p-3 mb-4">
                                                <h4 className="text-sm font-semibold text-blue-900 mb-2">Learning
                                                    Outcome:</h4>
                                                <p className="text-sm text-blue-800">{lesson.outcome1}</p>
                                            </div>
                                        )}

                                        {/* Action Button */}
                                        <button
                                            onClick={() => viewLesson(lesson.id)}
                                            className="w-full bg-[#D67C2C] text-white py-3 px-4 rounded-lg hover:bg-[#c06a25] transition-colors flex items-center justify-center gap-2 font-medium"
                                        >
                                            <Eye className="w-4 h-4"/>
                                            View Lesson
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Pagination */}
                    {!loading && !error && lessons.length > 0 && totalPages > 1 && (
                        <div className="flex justify-center items-center gap-4 mt-8">
                            <button
                                onClick={() => setPage(page - 1)}
                                disabled={page === 1}
                                className={`px-4 py-2 rounded-lg ${page === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                            >
                                Previous
                            </button>

                            <span className="text-sm text-gray-600">
                                Page {page} of {totalPages}
                            </span>

                            <button
                                onClick={() => setPage(page + 1)}
                                disabled={page === totalPages}
                                className={`px-4 py-2 rounded-lg ${page === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Lesson Detail Modal */}
            {showLessonModal && selectedLesson && (
                <div
                    className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex justify-center items-start p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl mt-8 mb-8">
                        {/* Modal Header */}
                        <div
                            className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-orange-50 to-red-50">
                            <div className="flex items-center gap-4">
                                <div
                                    className="w-16 h-16 rounded-2xl flex items-center justify-center bg-gradient-to-br from-orange-500 to-orange-500 shadow-lg">
                                    {getDomainIcon(selectedLesson.domain)}
                                </div>
                                <div>
                                    <h3 className="text-2xl font-bold text-gray-900">
                                        {selectedLesson.publishSettings?.publishedTitle || selectedLesson.themeTopic || 'Untitled Lesson'}
                                    </h3>
                                    <p className="text-gray-600">
                                        {selectedLesson.domain || 'General'} • {selectedLesson.classGrade ? `Grade ${selectedLesson.classGrade}` : 'All Grades'} • {selectedLesson.duration || 'Duration not specified'}
                                    </p>
                                    <p className="text-sm text-gray-500 mt-1">
                                        {selectedLesson.publishSettings?.publishedDescription}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowLessonModal(false)}
                                className="p-3 hover:bg-white hover:bg-opacity-50 rounded-xl transition-colors"
                            >
                                <X className="w-6 h-6 text-gray-500"/>
                            </button>
                        </div>

                        <div className="p-6 max-h-[70vh] overflow-y-auto">
                            {/* Learning Outcomes */}
                            {(selectedLesson.outcome1 || selectedLesson.outcome2 || selectedLesson.outcome3) && (
                                <div className="bg-blue-50 rounded-xl p-6 mb-6">
                                    <h4 className="text-lg font-bold text-blue-900 mb-4 flex items-center gap-2">
                                        <Target className="w-5 h-5"/>
                                        Learning Outcomes
                                    </h4>
                                    <div className="space-y-2">
                                        {selectedLesson.outcome1 && (
                                            <div className="flex items-start gap-2">
                                                <ChevronRight className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0"/>
                                                <p className="text-blue-800">{selectedLesson.outcome1}</p>
                                            </div>
                                        )}
                                        {selectedLesson.outcome2 && (
                                            <div className="flex items-start gap-2">
                                                <ChevronRight className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0"/>
                                                <p className="text-blue-800">{selectedLesson.outcome2}</p>
                                            </div>
                                        )}
                                        {selectedLesson.outcome3 && (
                                            <div className="flex items-start gap-2">
                                                <ChevronRight className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0"/>
                                                <p className="text-blue-800">{selectedLesson.outcome3}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Lesson Content */}
                            <div className="mb-6">
                                <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                    <BookOpen className="w-5 h-5 text-green-600"/>
                                    Lesson Content
                                </h4>
                                {renderLessonContent(selectedLesson)}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default StudentLessons;